import { Kampanya, KampanyaDurumu } from "@/mocks/kampanyalar";

interface KampanyaKartiProps {
  kampanya: Kampanya;
  onDetay: () => void;
  onBaslat?: () => void;
  onDurdur?: () => void;
  onKopyala: () => void;
  onSil: () => void;
}

function DurumBadge({ durum }: { durum: KampanyaDurumu }) {
  const config = {
    Aktif: "bg-emerald-100 text-emerald-700",
    Taslak: "bg-gray-100 text-gray-600",
    Durduruldu: "bg-amber-100 text-amber-700",
    Tamamlandı: "bg-sky-100 text-sky-700",
  };
  const dots = {
    Aktif: "bg-emerald-500",
    Taslak: "bg-gray-400",
    Durduruldu: "bg-amber-500",
    Tamamlandı: "bg-sky-500",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config[durum]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dots[durum]}`}></span>
      {durum}
    </span>
  );
}

function TipBadge({ tip }: { tip: string }) {
  const colors: Record<string, string> = {
    "Referans Kampanyası": "bg-violet-100 text-violet-700",
    "Ödeme Hatırlatma": "bg-amber-100 text-amber-700",
    "Taahhüt Bilgilendirme": "bg-orange-100 text-orange-700",
    "Kampanya Duyurusu": "bg-pink-100 text-pink-700",
    "Sistem Güncelleme": "bg-sky-100 text-sky-700",
    "Yeni Kayıt Takibi": "bg-emerald-100 text-emerald-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${colors[tip] || "bg-gray-100 text-gray-600"}`}>
      {tip}
    </span>
  );
}

export default function KampanyaKarti({ kampanya, onDetay, onBaslat, onDurdur, onKopyala, onSil }: KampanyaKartiProps) {
  const progress = kampanya.toplamKisi > 0 ? Math.round((kampanya.gonderilen / kampanya.toplamKisi) * 100) : 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 hover:border-gray-200 transition-all group overflow-hidden">
      {/* Top strip color */}
      <div className={`h-1 w-full ${
        kampanya.durum === "Aktif" ? "bg-emerald-400" :
        kampanya.durum === "Tamamlandı" ? "bg-sky-400" :
        kampanya.durum === "Durduruldu" ? "bg-amber-400" :
        "bg-gray-200"
      }`} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-gray-900 truncate">{kampanya.ad}</h3>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <TipBadge tip={kampanya.tip} />
              <DurumBadge durum={kampanya.durum} />
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">{kampanya.aciklama}</p>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            { label: "Toplam", value: kampanya.toplamKisi, color: "text-gray-800" },
            { label: "Gönderilen", value: kampanya.gonderilen, color: "text-emerald-600" },
            { label: "Bekleyen", value: kampanya.bekleyen, color: "text-amber-600" },
            { label: "Hatalı", value: kampanya.hatali, color: "text-red-500" },
          ].map((s) => (
            <div key={s.label} className="text-center bg-gray-50 rounded-xl py-2">
              <p className={`text-base font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-gray-400">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-500">İlerleme</span>
            <span className="text-xs font-semibold text-gray-700">%{progress}</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                kampanya.durum === "Aktif" ? "bg-emerald-400" :
                kampanya.durum === "Tamamlandı" ? "bg-sky-400" :
                kampanya.durum === "Durduruldu" ? "bg-amber-400" :
                "bg-gray-300"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Dates */}
        <div className="flex items-center justify-between text-[11px] text-gray-400 mb-4">
          <span>
            <span className="text-gray-500 font-medium">Başlangıç:</span> {kampanya.baslamaTarihi}
          </span>
          <span>
            <span className="text-gray-500 font-medium">Son:</span> {kampanya.sonGonderim}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
          <button
            onClick={onDetay}
            className="flex-1 py-2 text-xs font-medium text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-1.5"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-eye-line text-xs"></i>
            </div>
            Detay
          </button>

          {kampanya.durum === "Taslak" || kampanya.durum === "Durduruldu" ? (
            <button
              onClick={onBaslat}
              className="flex-1 py-2 text-xs font-medium text-white bg-emerald-500 rounded-xl hover:bg-emerald-600 cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-1.5"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-play-line text-xs"></i>
              </div>
              Başlat
            </button>
          ) : kampanya.durum === "Aktif" ? (
            <button
              onClick={onDurdur}
              className="flex-1 py-2 text-xs font-medium text-amber-700 bg-amber-100 rounded-xl hover:bg-amber-200 cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-1.5"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-pause-line text-xs"></i>
              </div>
              Durdur
            </button>
          ) : null}

          <button
            onClick={onKopyala}
            className="w-8 h-8 flex items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors"
            title="Kopyala"
          >
            <i className="ri-file-copy-line text-sm"></i>
          </button>
          <button
            onClick={onSil}
            className="w-8 h-8 flex items-center justify-center rounded-xl border border-red-100 text-red-400 hover:bg-red-50 cursor-pointer transition-colors"
            title="Sil"
          >
            <i className="ri-delete-bin-line text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}